import { useEffect, useState } from 'react';
import type { ReceiptData } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatMoney, formatQty, UNIT_LABEL } from '../format';
import { AUTOPRINT_KEY, PAPER_KEY, Paper, printCss, receiptRows, paperChars } from '../receipt';

interface Props {
  id: string;
  copy?: boolean;
  autoPrint?: boolean;
}

function readPaper(): Paper {
  try {
    const v = localStorage.getItem(PAPER_KEY);
    return v === '58' || v === 'A4' ? v : '80';
  } catch {
    return '80';
  }
}

// Çek/qaimə: 58/80 mm qəbz və A4 qaimə. Çap brauzer çapıdır; kağız eni per-viewer seçimdir (localStorage).
export function Receipt({ id, copy = false, autoPrint = false }: Props) {
  const [data, setData] = useState<ReceiptData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paper, setPaper] = useState<Paper>(readPaper);

  useEffect(() => {
    api
      .receipt(id, copy)
      .then(setData)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Çek yüklənmədi'));
  }, [id, copy]);

  useEffect(() => {
    if (!data || !autoPrint) return;
    try {
      if (localStorage.getItem(AUTOPRINT_KEY) === '1') window.print();
    } catch {
      /* yaddaş yoxdursa avtomatik çap yoxdur */
    }
  }, [data, autoPrint]);

  const choosePaper = (p: Paper) => {
    setPaper(p);
    try {
      localStorage.setItem(PAPER_KEY, p);
    } catch {
      /* yaddaşda saxlanmır — seçim cari baxışda qalır */
    }
  };

  return (
    <section className="card receipt-wrap">
      <style>{`@media print { body * { visibility: hidden; } .receipt-print, .receipt-print * { visibility: visible; } .receipt-print { position: absolute; left: 0; top: 0; width: 100%; } .no-print { display: none !important; } } ${printCss(paper)}`}</style>
      <div className="toolbar no-print">
        <label>
          Kağız
          <select value={paper} onChange={(e) => choosePaper(e.target.value as Paper)}>
            <option value="58">58 mm</option>
            <option value="80">80 mm</option>
            <option value="A4">A4 qaimə</option>
          </select>
        </label>
        <button type="button" className="btn-small" onClick={() => window.print()} disabled={!data}>
          Çap et
        </button>
      </div>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {data && paper !== 'A4' && (
        <pre className="receipt-print receipt-pre" style={{ width: `${paperChars(paper)}ch` }}>
          {receiptRows(data, paperChars(paper)).join('\n')}
        </pre>
      )}
      {data && paper === 'A4' && (
        <div className="receipt-print receipt-a4">
          <h2>{data.kind === 'QAY' ? 'Qaytarma qaimə' : 'Satış qaimə'} {data.number}{data.copy ? ' — TƏKRAR' : ''}</h2>
          <p>
            <strong>Satıcı:</strong> {data.company.name}
            {data.company.voen ? `, VÖEN ${data.company.voen}` : ''}
            {data.company.address ? `, ${data.company.address}` : ''}
          </p>
          {data.customer && (
            <p>
              <strong>Alıcı:</strong> {data.customer.name}
              {data.customer.voen ? `, VÖEN ${data.customer.voen}` : ''}
              {data.customer.address ? `, ${data.customer.address}` : ''}
            </p>
          )}
          <table className="receipt-table">
            <thead>
              <tr>
                <th>Məhsul</th>
                <th>Miqdar</th>
                <th>Qiymət</th>
                <th>Endirim</th>
                <th>Məbləğ</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.map((l, i) => (
                <tr key={i}>
                  <td>{l.name}</td>
                  <td>
                    {formatQty(l.qty)} {UNIT_LABEL[l.unit] ?? l.unit}
                  </td>
                  <td>{formatMoney(l.unitPrice)}</td>
                  <td>{formatMoney(l.discount)}</td>
                  <td>{formatMoney(l.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            Cəm: <strong>{formatMoney(data.total)}</strong>
            {data.vatPayer ? ` · ƏDV daxil: ${formatMoney(data.vatAmount)}` : ''}
          </p>
          <p>Sözlə: {data.totalWords}</p>
          {/* İmza yerləri (KT-95) */}
          <div className="sign-row">
            <span>Təhvil verdi: ____________________</span>
            <span>Təhvil aldı: ____________________</span>
          </div>
          <p className="muted small">{data.footer}</p>
        </div>
      )}
    </section>
  );
}
